import type { BatchItem, BatchResult } from '@generalTypes/sriTypes';
import { endpoints } from '@store/api/apiConfig';
import { UPDATE_DOCUMENT_VIEW_MODEL } from '@store/constants/actionTypes';
import { groupBy } from 'lodash';
import { debounce, put, select } from 'redux-saga/effects';
import { selectValidationRulesThatNeedData } from './asyncValidationLoadDataSelectors';
import { loadValidationDataForBatch, setValidationDataFromBatch } from './validationState';
import type { ValidationRuleName } from './validationTypes';

function* handleValidationBatchSaga() {
  try {
    const dataItems = [] as Array<{
      href: string;
      validationRuleName: ValidationRuleName;
      batchItem: BatchItem<'GET'>;
      validityHash: string;
    }>;

    const validationsThatNeedData = yield select(selectValidationRulesThatNeedData);

    for (const { href, validation } of validationsThatNeedData) {
      const selectorResult: BatchItem<'GET'> | null = yield select((state) =>
        validation.selectDataBatch(state, href),
      );
      const validityHash: string = yield select((state) =>
        validation.selectInvalidateDataOnChangeOf?.(state, href),
      ) || '';
      if (selectorResult !== null) {
        dataItems.push({
          href,
          validationRuleName: validation.getName(),
          batchItem: selectorResult,
          validityHash,
        });
      }
    }

    if (!dataItems.length) {
      return;
    }

    const batch = dataItems.map((z) => z.batchItem);
    yield put(loadValidationDataForBatch({ items: dataItems }));

    const groupedBatches = groupBy(batch, (z) => z.href.split('?')?.[0]);
    /* As the validations can point to different endpoints, we need to group the batch items by their base url
     * and take into account if it supports batch (content-api/search-api) */
    for (const [baseUrl, group] of Object.entries(groupedBatches)) {
      const api = endpoints.get(baseUrl);
      const responseMap = new Map();
      if (api) {
        if (api.supportsBatch) {
          const response: BatchResult = yield api.client.put('/batch', group);

          for (const { href, validationRuleName, batchItem, validityHash } of dataItems) {
            /**
             * here we map the batch response to what it would look like for a regular contentApi.getAll
             * This might not always be what you prefer. depending on what call is being done in the batch.
             * if we need a custom mapping for some validation, we can add an optional mapping function to the validation rule.
             * this could also be useful if you want to add some extra data to the validation result.
             * for example: a timestamp of when this data was fetched, so you can make the data expire,
             * or some value of your content-node, at the time of fetching the validation data.
             */

            const data = response
              .find((z) => z.href === batchItem.href)
              ?.body?.results?.map((z) => z.$$expanded);
            if (data) {
              responseMap.set(validationRuleName + href, { href, validationRuleName, data, validityHash });
            }
          }
        } else {
          const hrefs = [...new Set(group.map((z) => z.href))]; // avoid doing multiple calls to the same href
          for (const href of hrefs) {
            const response = yield api.client.getRaw(href);
            dataItems
              .filter((z) => z.batchItem.href === href)
              .forEach((dataItem) => {
                responseMap.set(dataItem.href, {
                  href: dataItem.href,
                  validationRuleName: dataItem.validationRuleName,
                  data: response?.results,
                  validityHash: dataItem.validityHash, // we send the hash again, to prevent race conditions
                });
              });
          }
        }

        yield put(setValidationDataFromBatch(Array.from(responseMap.values())));
      } else {
        console.error(`No api found for baseUrl ${baseUrl}.`);
      }
    }

    yield put({ type: UPDATE_DOCUMENT_VIEW_MODEL });
  } catch (e) {
    console.log(e);
  }
}

export function* validationWatcherSaga() {
  yield debounce(250, '*', handleValidationBatchSaga);
}
